import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Filters,
  Kanban,
  KanbanCardBody,
  KanbanTag,
  Page,
  PageHeader,
  Table,
  Toolbar,
  type FilterValues,
  type KanbanColor,
  type KanbanStage,
} from '@evogard/ui';
import type { ListRevenuesQuery, RevenueSummary } from '@evogard/contract';
import {
  CHARGE_STATUS_LABELS,
  REVENUE_SOURCE_LABELS,
  REVENUE_SOURCE_TYPES,
  REVENUE_STATUSES,
  REVENUE_STATUS_LABELS,
  REVENUE_STATUS_TONES,
  format,
  formatDate,
  fromColumn,
  loadFailureMessage,
  type RevenueSourceType,
  type RevenueStatus,
} from '@evogard/domain';
import { LabelTags } from '../../../components/labels/LabelsPicker.js';
import { api, ok } from '../../../lib/api.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { RevenueFormModal } from './RevenueFormModal.js';
import { RevenueDesk } from './RevenueDesk.js';

/** A chave das consultas da receita — a mesa e o formulário invalidam por ela. */
export const REVENUES_KEY = ['financeiro', 'receitas'] as const;

/** A cor de cada coluna — a paleta de fase, pelo tom da situação. */
// @valor
const STAGE_COLORS: Readonly<Record<RevenueStatus, KanbanColor>> = {
  a_receber: 'azul',
  vencida: 'vermelho',
  recebida: 'verde',
  cancelada: 'cinza',
  estornada: 'amarelo',
};

const STAGES: KanbanStage[] = REVENUE_STATUSES.map((s) => ({
  id: s,
  name: REVENUE_STATUS_LABELS[s],
  color: STAGE_COLORS[s],
}));

const money = (v: string) => format(fromColumn(v)!);
const payerOf = (r: RevenueSummary) => r.payer.name ?? 'Sem nome';

function CardBody({ revenue: r }: { revenue: RevenueSummary }) {
  return (
    <KanbanCardBody
      tags={
        <>
          <LabelTags labels={r.labels} />
          <KanbanTag>{REVENUE_SOURCE_LABELS[r.sourceType]}</KanbanTag>
          {r.lastChargeStatus && (
            <KanbanTag>Asaas: {CHARGE_STATUS_LABELS[r.lastChargeStatus]}</KanbanTag>
          )}
        </>
      }
      title={
        <span className="flex items-start justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="shrink-0 text-texto-suave tabular-nums">#{r.id}</span>
            <span className="min-w-0 truncate">{payerOf(r)}</span>
          </span>
          <span className="shrink-0 tabular-nums">{money(r.amount)}</span>
        </span>
      }
      detail={
        <span className="tabular-nums">
          {r.receivedDate
            ? `recebida ${formatDate(r.receivedDate)}`
            : `vence ${formatDate(r.dueDate)}`}
        </span>
      }
      detailIcon="calendario"
      assignee={r.assignee?.name ?? null}
      alerts={
        <span className="line-clamp-2 text-xs text-texto-suave" title={r.description}>
          {r.description}
        </span>
      }
    />
  );
}

/**
 * As receitas da base (29/10/2026) — o que a casa tem a receber, independente
 * de fluxo e de banco. Tomou o lugar da tela de Cobranças: a cobrança no Asaas
 * virou um ato sobre a receita, e aparece dentro dela.
 *
 * O desenho é o do quadro de Despesas (a pedido do dono): uma coluna por
 * situação, a tabela como a outra vista, a busca e os filtros na barra, e o
 * cartão abre a mesa da receita (`RevenueDesk`), na moldura da mesa da despesa. Não se arrasta cartão: quem muda a situação é o ato
 * (cobrar, dar baixa, cancelar) ou a cobrança paga — nunca a coluna.
 *
 * A receita do sinistro (a PI do Periférico) aparece aqui e é movida pelo
 * acionamento; a avulsa é lançada, cobrada, baixada e cancelada daqui.
 */
export function RevenuesScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const canLaunch = can(context, 'finance.revenues', 'lancar');
  const [view, setView] = useState<'quadro' | 'tabela'>('quadro');
  const [searchText, setSearchText] = useState('');
  const search = useDebouncedTerm(searchText.trim());
  const [filters, setFilters] = useState<FilterValues>({});
  const [launching, setLaunching] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const text = (k: string) =>
    typeof filters[k] === 'string' && filters[k] ? filters[k] : undefined;
  const query: ListRevenuesQuery = {
    page: 1,
    ...(search ? { search } : {}),
    ...(text('origem') ? { sourceType: text('origem') as RevenueSourceType } : {}),
    ...(text('venceDe') ? { dueFrom: text('venceDe') } : {}),
    ...(text('venceAte') ? { dueTo: text('venceAte') } : {}),
  };

  const list = useQuery({
    queryKey: [...REVENUES_KEY, tenantId, 'lista', query],
    queryFn: async () =>
      ok(
        await api.revenues.listRevenues({ query }),
        200,
        loadFailureMessage(undefined, 'as receitas'),
      ),
    enabled: tenantId !== undefined,
  });
  const items = list.data?.items ?? [];

  return (
    <Page measure="cheia">
      <PageHeader
        title="Receitas"
        actions={
          canLaunch && (
            <Button icon="mais" onClick={() => setLaunching(true)}>
              Lançar receita
            </Button>
          )
        }
      >
        O que a casa tem a receber. A cobrança no Asaas é um ato sobre a receita, e a
        baixa vem da cobrança paga ou do comprovante.
      </PageHeader>
      <Toolbar
        search={{
          label: 'Pesquisar receita',
          placeholder: 'Quem paga, descrição, observação ou número',
          value: searchText,
          onChange: setSearchText,
        }}
        toolbar={
          <>
            <Filters
              values={filters}
              onApply={setFilters}
              fields={[
                {
                  type: 'selecao',
                  name: 'origem',
                  label: 'Origem',
                  placeholder: 'Todas',
                  options: REVENUE_SOURCE_TYPES.map((s) => ({
                    value: s,
                    label: REVENUE_SOURCE_LABELS[s],
                  })),
                },
                {
                  type: 'intervalo',
                  label: 'Vencimento',
                  from: 'venceDe',
                  to: 'venceAte',
                  kind: 'data',
                },
              ]}
            />
            <Button
              variant="contorno"
              icon={view === 'quadro' ? 'documento' : 'quadro'}
              onClick={() => setView(view === 'quadro' ? 'tabela' : 'quadro')}
            >
              {view === 'quadro' ? 'Ver tabela' : 'Ver quadro'}
            </Button>
          </>
        }
      />
      {list.data && list.data.total > items.length && (
        <Alert tone="alerta">
          Mostrando as {items.length} receitas mais novas de {list.data.total}. Use a
          busca ou os filtros para achar as outras.
        </Alert>
      )}
      {list.isError ? (
        <Alert tone="erro">{list.error.message}</Alert>
      ) : !list.data ? (
        <p className="text-sm text-texto-suave">Carregando as receitas…</p>
      ) : view === 'quadro' ? (
        <Kanban<RevenueSummary>
          label="Quadro de receitas"
          stages={STAGES}
          cardId={(r) => String(r.id)}
          cardLabel={(r) => `${payerOf(r)}, ${money(r.amount)}`}
          loadPage={async (stageId) => {
            const inStage = items.filter((r) => r.status === stageId);
            return { items: inStage, next: null, total: inStage.length };
          }}
          reloadKey={list.dataUpdatedAt}
          renderCard={(r) => <CardBody revenue={r} />}
          onOpenCard={(r) => setOpenId(r.id)}
          emptyColumnText="Nenhuma receita nesta situação."
        />
      ) : (
        <Card bleed>
          <Table<RevenueSummary>
            label="Receitas"
            items={items}
            rowKey={(r) => String(r.id)}
            rowActions={(r) => [
              { label: 'Abrir', icon: 'olho', run: () => setOpenId(r.id) },
            ]}
            empty="Nenhuma receita com esses filtros."
            columns={[
              {
                title: 'Nº',
                minWidth: '5rem',
                alignment: 'inicio',
                cell: (r) => <span className="tabular-nums">#{r.id}</span>,
              },
              {
                title: 'Quem paga',
                minWidth: '14rem',
                alignment: 'inicio',
                cell: (r) => (
                  <span className="flex flex-col">
                    <span className="text-texto">{payerOf(r)}</span>
                    <span className="text-sm text-texto-suave">
                      {r.payer.kind === 'associado' ? r.payer.code : 'Beneficiário'}
                    </span>
                  </span>
                ),
              },
              {
                title: 'Descrição',
                minWidth: '16rem',
                alignment: 'inicio',
                cell: (r) => (
                  <span className="flex flex-col">
                    <span className="text-texto">{r.description}</span>
                    {r.categoryName && (
                      <span className="text-sm text-texto-suave">{r.categoryName}</span>
                    )}
                  </span>
                ),
              },
              {
                title: 'Responsável',
                minWidth: '10rem',
                cell: (r) =>
                  r.assignee ? (
                    r.assignee.name
                  ) : (
                    <span className="text-texto-suave">Sem responsável</span>
                  ),
              },
              {
                title: 'Origem',
                minWidth: '7rem',
                cell: (r) => REVENUE_SOURCE_LABELS[r.sourceType],
              },
              {
                title: 'Situação',
                minWidth: '9rem',
                cell: (r) => (
                  <Badge tone={REVENUE_STATUS_TONES[r.status]}>
                    {REVENUE_STATUS_LABELS[r.status]}
                  </Badge>
                ),
              },
              {
                title: 'Cobrança',
                minWidth: '10rem',
                cell: (r) =>
                  r.lastChargeStatus ? (
                    CHARGE_STATUS_LABELS[r.lastChargeStatus]
                  ) : (
                    <span className="text-texto-suave">Sem cobrança</span>
                  ),
              },
              {
                title: 'Vencimento',
                minWidth: '7rem',
                cell: (r) => (
                  <span className="tabular-nums">{formatDate(r.dueDate)}</span>
                ),
              },
              {
                title: 'Valor',
                minWidth: '8rem',
                alignment: 'fim',
                cell: (r) => <span className="tabular-nums">{money(r.amount)}</span>,
              },
            ]}
          />
        </Card>
      )}

      {canLaunch && (
        <RevenueFormModal
          open={launching}
          onOpenChange={setLaunching}
          onLaunched={(id) => {
            setLaunching(false);
            setOpenId(id);
          }}
        />
      )}
      {openId !== null && <RevenueDesk id={openId} onClose={() => setOpenId(null)} />}
    </Page>
  );
}
